import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Card, Avatar, Chip, Skeleton } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import LayersIcon from "@mui/icons-material/Layers";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import MapIcon from "@mui/icons-material/Map";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { tokens } from "../../theme";
import { PageHead, pageContainerSx } from "../../components/ui";

const Kpi = ({ label, value, icon: Icon, loading }) => (
  <Card sx={{ p: "13px 14px", display: "flex", flexDirection: "column", gap: 1.25 }}>
    {loading ? (
      <>
        <Skeleton width={28} height={28} variant="rounded" />
        <Skeleton width="50%" height={30} />
      </>
    ) : (
      <>
        <Stack direction="row" alignItems="center" gap={1}>
          <Avatar
            variant="rounded"
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              background: tokens.surface3,
              color: tokens.text2,
            }}
          >
            <Icon sx={{ fontSize: 15 }} />
          </Avatar>
          <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: tokens.text2 }}>
            {label}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 26, fontWeight: 700, letterSpacing: "-.03em" }}>
          {value}
        </Typography>
      </>
    )}
  </Card>
);

// Stable keys for the placeholder cards (no venue data exists yet at that point).
const VENUE_SKELETON_KEYS = ["sk-1", "sk-2", "sk-3"];

const avatarColor = (name = "") => {
  const code = name.slice(0, 2).toUpperCase() || "V";
  const hue = (code.charCodeAt(0) * 37 + (code.charCodeAt(1) || 0) * 11) % 360;
  return { code, color: `hsl(${hue} 55% 45%)` };
};

const VenueCard = ({ venue, navigate }) => {
  const floorCount = venue.floors?.length || 0;
  // Backend-derived and already access-scoped to this user. No client-side fallback:
  // summing `venue.robots` / `floor.maps` reads stale back-reference arrays, which is
  // why this card used to disagree with the Maps and Robots pages.
  const robotCount = venue.robotCount ?? 0;
  const mapCount = venue.mapCount ?? 0;
  const { code, color } = avatarColor(venue.name);

  return (
    <Card sx={{ overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" gap={1.4} sx={{ p: 1.75 }}>
        <Avatar
          sx={{
            width: 38,
            height: 38,
            borderRadius: "10px",
            background: color,
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {code}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: tokens.text }}>
            {venue.name}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: tokens.text3 }}>Venues</Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          borderTop: `1px solid ${tokens.border}`,
        }}
      >
        {[
          ["Floors", floorCount],
          ["Maps", mapCount],
          ["Robots", robotCount],
        ].map(([label, val], i) => (
          <Box
            key={label}
            sx={{
              p: "11px 14px",
              borderRight: i < 2 ? `1px solid ${tokens.border}` : "none",
            }}
          >
            <Typography sx={{ fontSize: 11, color: tokens.text3 }}>{label}</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.25 }}>{val}</Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          p: 1.5,
          borderTop: `1px solid ${tokens.border}`,
          display: "flex",
          gap: 1,
        }}
      >
        <Chip
          icon={<MapIcon sx={{ fontSize: "14px !important" }} />}
          label="View maps"
          onClick={() =>
            navigate(`/maps?venue=${venue._id}&name=${encodeURIComponent(venue.name)}`)
          }
          variant="outlined"
          sx={{
            flex: 1,
            justifyContent: "center",
            cursor: "pointer",
            borderColor: tokens.border,
            color: tokens.text,
          }}
        />
        <Chip
          icon={<SmartToyIcon sx={{ fontSize: "14px !important" }} />}
          label="Robots"
          onClick={() => navigate("/robots")}
          variant="outlined"
          sx={{
            flex: 1,
            justifyContent: "center",
            cursor: "pointer",
            borderColor: tokens.border,
            color: tokens.text,
          }}
        />
      </Box>
    </Card>
  );
};

const Venue = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const token = localStorage.getItem("portalToken");
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/v1/venues/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setVenues(await res.json());
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    };
    fetchVenues();
  }, []);

  // KPI totals sum the same access-scoped, backend-derived per-venue counts the
  // cards show, so the page header can never disagree with the cards beneath it.
  const totalFloors = venues.reduce((s, v) => s + (v.floors?.length || 0), 0);
  const totalMaps = venues.reduce((s, v) => s + (v.mapCount ?? 0), 0);
  const totalRobots = venues.reduce((s, v) => s + (v.robotCount ?? 0), 0);

  // Three-state card grid: skeletons while loading, empty-state, then the real
  // cards. Extracted from a nested ternary in the JSX below; branch order and
  // output are unchanged.
  const renderVenueGrid = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 1.5,
          }}
        >
          {VENUE_SKELETON_KEYS.map((skeletonKey) => (
            <Card key={skeletonKey} sx={{ p: 1.75 }}>
              <Skeleton height={80} />
            </Card>
          ))}
        </Box>
      );
    }
    if (venues.length === 0) {
      return (
        <Card sx={{ p: 5, textAlign: "center" }}>
          <BusinessIcon sx={{ fontSize: 40, color: tokens.text3, mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.text2 }}>
            No venues yet
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.text3, mt: 0.5 }}>
            Venues assigned by Botriq will appear here.
          </Typography>
        </Card>
      );
    }
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 1.5,
        }}
      >
        {venues.map((v) => (
          <VenueCard key={v._id} venue={v} navigate={navigate} />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={pageContainerSx()}>
      <PageHead
        title="Venues"
        sub="Properties where your robots operate · view only"
        icon="store"
        actions={
          <Chip
            label="View only"
            size="small"
            sx={{ background: tokens.surface3, color: tokens.text2 }}
          />
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 1.5,
          mb: 1.75,
        }}
      >
        <Kpi label="Venues" value={venues.length} icon={BusinessIcon} loading={loading} />
        <Kpi label="Total floors" value={totalFloors} icon={LayersIcon} loading={loading} />
        <Kpi label="Maps" value={totalMaps} icon={MapIcon} loading={loading} />
        <Kpi label="Robots deployed" value={totalRobots} icon={SmartToyIcon} loading={loading} />
      </Box>

      {renderVenueGrid()}
    </Box>
  );
};

Kpi.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  icon: PropTypes.elementType.isRequired,
  loading: PropTypes.bool,
};

VenueCard.propTypes = {
  venue: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    floors: PropTypes.array,
    mapCount: PropTypes.number,
    robotCount: PropTypes.number,
  }).isRequired,
  navigate: PropTypes.func.isRequired,
};

export default Venue;
